import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import { usePageStore } from "@/hooks/usePageStore";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import {
  useGetSiteOfficeClosure,
  useGetSiteOfficeHoursSchedule,
  useSaveSchedule,
} from "@/hooks/useSiteOfficeHours";
import {
  SiteOfficeHoursFiltersComponent,
  SiteOfficeHoursFilters,
  defaultSiteOfficeHoursFilters,
} from "@/ui/content/siteOfficeHours/filters";
import { ClosureSection } from "@/ui/content/siteOfficeHours/closureSection";
import SetClosureModal from "@/ui/modals/siteOfficeHours/SetClosureModal";
import {
  WeeklySchedule,
  DayState,
  buildDefaultSchedule,
  buildScheduleFromApi,
} from "@/ui/content/siteOfficeHours/weeklySchedule";
import GenericAlertModal from "@/components/AlertModal";

const SiteOfficeHoursPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { userInfo, userHasAccessToCode, hasSystemAdminRole } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const { showSnackBar } = useFeedBack();


  const auditLogViewCalledRef = useRef(false);

  const [filters, setFilters] = useState<SiteOfficeHoursFilters>(
    defaultSiteOfficeHoursFilters
  );
  const [schedule, setSchedule] = useState<DayState[]>(buildDefaultSchedule());
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [unsavedWarning, setUnsavedWarning] = useState<{
    open: boolean;
    onConfirm: () => void;
  }>({ open: false, onConfirm: () => {} });
  const [genericAlert, setGenericAlert] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [closureModalOpen, setClosureModalOpen] = useState(false);

  const { programs, isLoadingPrograms, isFetchingPrograms } =
    useGetUserPrograms({
      userId: userInfo?._id as string,
      hasSystemAdminRole,
    });

  const hasNoContent = useMemo(() => {
    if (isLoadingPrograms || isFetchingPrograms) return false;
    if (!programs || programs.length === 0) return true;
    const activeOrgUnits = programs.flatMap(
      (p) => p.orgUnitIds?.filter((ou) => ou.status === "ACTIVE") ?? []
    );
    return activeOrgUnits.length === 0;
  }, [programs, isLoadingPrograms, isFetchingPrograms]);

  const { schedule: apiSchedule, isLoadingSchedule } =
    useGetSiteOfficeHoursSchedule({
      programId: filters.programId,
      orgUnitId: filters.orgUnitId,
      enabled: !!filters.programId && !!filters.orgUnitId,
    });

  const { closure, isLoadingClosure, isFetchingClosure } =
    useGetSiteOfficeClosure({
      programId: filters.programId,
      orgUnitId: filters.orgUnitId,
      enabled: !!filters.programId && !!filters.orgUnitId,
    });

  const saveScheduleMutation = useSaveSchedule();

  // Single source of truth for schedule state — driven entirely by apiSchedule
  // undefined = query in-flight (filters just changed), null = no saved schedule, Object = load from API
  useEffect(() => {
    if (isDirty) return;
    if (apiSchedule === undefined) {
      setSchedule(buildDefaultSchedule());
    } else if (apiSchedule && apiSchedule.days) {
      setSchedule(buildScheduleFromApi(apiSchedule.days));
    } else if (apiSchedule === null) {
      setSchedule(buildDefaultSchedule());
    }
  }, [apiSchedule, isDirty]);

  // When filters change, only reset the dirty flag — schedule is handled above via apiSchedule
  useEffect(() => {
    setIsDirty(false);
  }, [filters.programId, filters.orgUnitId]);

  useEffect(() => {
    setPageTitle("Site Office Hours & Closure");
  }, []);

  // Audit log on enter
  useEffect(() => {
    if (
      userInfo &&
      filters.programId &&
      filters.orgUnitId &&
      !auditLogViewCalledRef.current
    ) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.CLOSURE_AND_HOURS,
        actionCode: ACTION_CODE.WEB_SETTINGS_CLOSURE_HOURS_ENTER,
        action: "When entering",
        detail: "Entered the closure and hours setting",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo._id as string,
        programId: filters.programId,
        orgUnitId: filters.orgUnitId,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo, filters.programId, filters.orgUnitId]);

  const handleInsufficientAccess = () => {
    setGenericAlert({
      open: true,
      title: "Insufficient permissions",
      message:
        "You do not have permission to perform this action. Contact your administrator.",
    });
  };

  const handleScheduleChange = (updated: DayState[]) => {
    setSchedule(updated);
    setIsDirty(true);
  };

  const handleFilterChange = (
    newFilters: React.SetStateAction<SiteOfficeHoursFilters>
  ) => {
    const resolve =
      typeof newFilters === "function" ? newFilters(filters) : newFilters;

    if (isDirty) {
      setUnsavedWarning({
        open: true,
        onConfirm: () => {
          setIsDirty(false);
          setFilters(resolve);
          setUnsavedWarning({ open: false, onConfirm: () => {} });
        },
      });
    } else {
      setFilters(resolve);
    }
  };

  const handleSaveConfirm = async () => {
    setShowConfirmSave(false);
    if (!userHasAccessToCode("SETTINGS_CLOSURE_SAVE")) {
      handleInsufficientAccess();
      return;
    }

    const days = schedule.map((d) =>
      d.enabled
        ? { dayOfWeek: d.dayOfWeek, enabled: true as const, openTime: d.openTime, closeTime: d.closeTime }
        : { dayOfWeek: d.dayOfWeek, enabled: false as const, openTime: d.openTime, closeTime: d.closeTime }
    );

    await saveScheduleMutation.mutateAsync(
      {
        programId: filters.programId,
        orgUnitId: filters.orgUnitId,
        userId: userInfo?._id as string,
        days,
      },
      {
        onSuccess: () => {
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.SETTINGS,
            option: OPTIONS.WEEKLY_HOURS,
            actionCode: ACTION_CODE.WEB_SETTINGS_HOURS_SAVE,
            action: "When saving",
            detail: "Saved the weekly office hours schedule",
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            programId: filters.programId,
            orgUnitId: filters.orgUnitId,
          });
          setIsDirty(false);
          showSnackBar("Weekly schedule saved successfully.", "success");
        },
        onError: () => {
          showSnackBar("Failed to save the schedule. Please try again.", "error");
        },
      }
    );
  };

  const handleSaveRequest = () => {
    if (!userHasAccessToCode("SETTINGS_CLOSURE_SAVE")) {
      handleInsufficientAccess();
      return;
    }
    setShowConfirmSave(true);
  };

  const handleEditClosureClick = () => {
    setClosureModalOpen(true);
  };

  const isLoadingData = isLoadingSchedule || isLoadingClosure;

  return (
    <>
      <Box mt={0}>
        <Card sx={{ width: "100%", borderRadius: "0"}} elevation={0}>
          <CardContent
            sx={{ display: "flex", flexDirection: "column", rowGap: "1rem", pt: 0 }}
          >
            <Box >
              <Typography variant="titleSmall" color="menu.dark" fontWeight={600}>
                Site Office Hours &amp; Closure
              </Typography>
            </Box>
            <Box>
              <Typography variant="bodyMedium" color="icon.dark" mt={0.5}>
                Configure standard operating hours per site and manage temporary
                emergency closures.
              </Typography>
            </Box>

            {hasNoContent ? (
              <Typography variant="bodyMedium" >
                No programs or sites are configured yet.
              </Typography>
            ) : (
              <>
                <SiteOfficeHoursFiltersComponent
                  filtersState={[filters, handleFilterChange]}
                  programs={programs}
                  isLoadingPrograms={isLoadingPrograms}
                  isFetchingPrograms={isFetchingPrograms}
                />

                <ClosureSection
                  closure={closure}
                  isLoading={isLoadingClosure || isFetchingClosure}
                  onEditClick={() => {
                    if (userHasAccessToCode("SETTINGS_CLOSURE_SAVE")) {
                      handleEditClosureClick();
                    } else {
                      handleInsufficientAccess();
                    }
                  }}
                />

                <Divider />

                <WeeklySchedule
                  schedule={schedule}
                  onChange={handleScheduleChange}
                  onSave={handleSaveRequest}
                  isLoading={isLoadingData}
                  isSaving={saveScheduleMutation.isPending}
                  apiSchedule={apiSchedule}
                    canSave={
                    !!filters.programId &&
                    !!filters.orgUnitId
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {showConfirmSave && (
        <GenericAlertModal
          title="Save Weekly Schedule"
          description="Are you sure you want to save the weekly schedule for the selected program and site?"
          theme={theme}
          onCancel={() => setShowConfirmSave(false)}
          onClick={handleSaveConfirm}
          submitText="Confirm"
          cancelText="Cancel"
        />
      )}

      {unsavedWarning.open && (
        <GenericAlertModal
          title="Unsaved Changes"
          description="You have modified the weekly hours but have not been saved. You will lost the changes. Are you sure you want to continue?"
          theme={theme}
          onCancel={() =>
            setUnsavedWarning({ open: false, onConfirm: () => {} })
          }
          onClick={unsavedWarning.onConfirm}
          submitText="Continue"
          cancelText="Stay"
        />
      )}

      {genericAlert.open && (
        <GenericAlertModal
          title={genericAlert.title}
          description={genericAlert.message}
          theme={theme}
          onClick={() =>
            setGenericAlert((prev) => ({ ...prev, open: false }))
          }
          submitText="Close"
        />
      )}

      <SetClosureModal
        open={closureModalOpen}
        onClose={() => setClosureModalOpen(false)}
        programId={filters.programId}
        orgUnitId={filters.orgUnitId}
        existingClosure={closure}
      />
    </>
  );
};

export default SiteOfficeHoursPage;
